"use client";

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { NavigationItem } from "@/navigation/types";
import { useTheme } from "@/context/ThemeContext";
import logoMain from "@/assets/images/nscs_logo_color.svg";
import logoMainWhite from "@/assets/images/nscs_logo_white.svg";
import navConfig from "@/navigation/navigation";

import {
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { ChevronRightIcon } from "@heroicons/react/24/outline";

interface MobileSideNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function MobileSideNav({
  open,
  onOpenChange,
}: MobileSideNavProps) {
  const { theme } = useTheme();
  const navItems = navConfig.navigation;

  // State for menu toggling & submenus
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  function handleToggleItem(itemKey: string) {
    setOpenItems((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
  }

  function renderNavigation(items: NavigationItem[]) {
    return items.map((item) => {
      const itemKey = item.internalName || item.name;
      const hasChildren = !!item.children?.length;
      const isOpen = !!openItems[itemKey];

      if (hasChildren) {
        return (
          <SidebarMenuItem
            data-state={isOpen ? "open" : undefined}
            key={itemKey}
          >
            <SidebarMenuButton
              data-state={isOpen ? "open" : undefined}
              onClick={() => handleToggleItem(itemKey)}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.name}</span>
              <ChevronRightIcon
                aria-hidden="true"
                className={`ml-auto h-4 w-4 transition-transform ${
                  isOpen ? "rotate-90" : ""
                }`}
              />
            </SidebarMenuButton>
            {isOpen && (
              <SidebarMenuSub>
                {renderNavigation(item.children!)}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        );
      }

      return (
        <SidebarMenuItem key={itemKey}>
          <SidebarMenuButton asChild>
            <Link onClick={() => onOpenChange(false)} to={item.href ?? "#"}>
              <item.icon className="h-4 w-4 shrink-0" />
              <span>{item.name}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });
  }

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        className="m-0 h-screen w-screen overflow-auto bg-sidebar p-0 text-sidebar-foreground"
        side="top"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Mobile Menu</SheetTitle>
          <SheetDescription>Slides in from the top</SheetDescription>
        </SheetHeader>

        <SidebarHeader className="border-b border-sidebar-border">
          <div className="flex flex-col items-start space-y-3 px-2 py-2">
            <img
              alt="Your Company"
              className="h-8 w-auto"
              src={theme === "dark" ? logoMainWhite : logoMain}
            />
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>{renderNavigation(navItems)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarSeparator />

        <SidebarFooter className="border-t border-sidebar-border">
          <div className="px-2">
            <span className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} Your Company. All rights
              reserved.
            </span>
          </div>
        </SidebarFooter>
      </SheetContent>
    </Sheet>
  );
}
